import {Mutex} from '../utils/mutex';
import {BBModel} from '../bb_model';
import cloneDeep from 'lodash/fp/cloneDeep';
import {emitter} from '../utils/emitter';

class LiveStatus {

    constructor(company) {
        this.PUSHER_CHANNEL_EVENT = 'live_status';
        this.company = cloneDeep(company);
        this.monitor = {count: 0};

        this.updateLiveStatusFromAddQueuer = this.updateLiveStatusFromAddQueuer.bind(this);
    }

    subscribe() {
        this.company.pusherChannelPromise.then((pusherChannel) => {
            this.bindToChannel(pusherChannel);
        });
    }

    unsubscribe() {
        this.company.pusherChannelPromise.then((pusherChannel) => {
            this.undbindFromChannel(pusherChannel);
        });
    }

    undbindFromChannel(channel) {
        channel.unbind(this.PUSHER_CHANNEL_EVENT);
    }

    bindToChannel(channel) {
        channel.bind(this.PUSHER_CHANNEL_EVENT, (push) => {
            if (!this.waiting_on_update) {
                return this.singleInstanceUpdate();
            } else {
                this.queue_live_status_request = true;
            }
        }, this);
    }

    singleInstanceUpdate() {
        this.waiting_on_update = true;

        return this.updateStatus()
            .finally(() => {
                this.waiting_on_update = false;
                return this.callQueuedRequest();
            });
    }

    callQueuedRequest() {
        if (this.queue_live_status_request) {
            this.queue_live_status_request = false;

            return this.singleInstanceUpdate();
        }
    }

    emitError(err) {
        emitter.emit('conciergeAction:fail', err);
    }

    // fetch an updated status from the server
    updateStatus() {
        this.company.$flush('live_status');

        this.promise = new Promise((resolve, reject) => {
            return this.company.$get('live_status').then((live_status) => {
                this.parseStatus(live_status);
                resolve();
            }).catch(reject);
        });

        return this.promise;
    }

    // parse a returned status from an update
    parseStatus(live_status) {
        this.live_status = live_status;
        const status = live_status.status;

        this.people_map = {};
        this.queues_map = {};
        this.queuers_map = {};
        this.queues = status.queues;
        this.people = status.people;
        this.queuers = [];
        this.updated_at = status.updated_at;

        for (const queuer of status.queuers) {
            const new_queuer = new BBModel.Admin.Queuer(queuer);
            this.queuers.push(new_queuer);
        }

        for (const person of this.people) {
            this.people_map[person.id] = person;
            person.waiting_queuers = [];
        }
        for (const queue of this.queues) {
            this.queues_map[queue.id] = queue;
        }
        for (const queuer of this.queuers) {
            this.queuers_map[queuer.id] = queuer;
            // update the people with list of people waiting to see specific them - or keep track of someone they are serving
            if (queuer.person_id) {

                if (queuer.position === 0) {
                    this.people_map[queuer.person_id].serving = queuer;
                } else {
                    this.people_map[queuer.person_id].waiting_queuers.push(queuer);
                }
            }
        }
        // update a counter each time we update the live status - so anything watching can know about it
        this.monitor.count += 1;
    }

    // look up a status for a specific person
    Person(person) {
        return this.people_map[person.id];
    }

    // look up the status for a specific queue
    Queue(queue) {
        return this.queues_map[queue.id];
    }

    // call the endpoint to update a staff member status
    async setAttendance(person, status, duration, queueId) {
        try {
            this.waiting_on_update = true;
            const socket_id = await this.getSocketId();
            const bodyParams = {
                person_id: person.id,
                status,
                estimated_duration: duration,
                client_queue_id: queueId,
                socket_id
            };
            const response = await person.$put('attendance', {live_status: true}, bodyParams);
            const live_status = await response.$get('live_status');
            this.parseStatus(live_status);
            return this;
        } catch (err) {
            this.emitError(err);
            throw err;
        } finally {
            this.waiting_on_update = false;
        }
    }

    async startServing(person, queueId, queuer) {
        this.waiting_on_update = true;
        const mutex = await Mutex.getLock();
        try {
            const socket_id = await this.getSocketId();
            const params = {
                queuer_id: queuer ? queuer.id : null,
                client_queue_id: queueId,
                person_id: person.id,
                socket_id
            };
            const response = await this.live_status.$post('start_serving', {live_status: true}, params);
            const live_status = await response.$get('live_status');
            this.parseStatus(live_status);
            return this;
        } catch (err) {
            this.emitError(err);
            throw err;
        } finally {
            this.waiting_on_update = false;
            Mutex.unlock(mutex);
        }
    }

    async finishServing(person, status) {
        this.waiting_on_update = true;
        const mutex = await Mutex.getLock();
        try {
            // immediately blank them and mark them as available
            this.people_map[person.id].status = 1;
            const serving = this.people_map[person.id].serving;
            this.people_map[person.id].serving = null;
            const socket_id = await this.getSocketId();
            const params = {
                live_status: true,
                queuer_id: serving.id,
                status,
                socket_id
            };
            const response = await this.live_status.$post('finish_serving', params);
            const live_status = await response.$get('live_status');
            this.parseStatus(live_status);
            return this;
        } catch (err) {
            this.emitError(err);
            throw err;
        } finally {
            this.waiting_on_update = false;
            Mutex.unlock(mutex);
        }
    }

    async extendAppointment(person, minutes) {
        this.waiting_on_update = true;
        const mutex = await Mutex.getLock();
        try {
            const serving = this.people_map[person.id].serving;

            let new_duration;
            if (serving.end.isBefore(moment())) {
                const d = moment.duration(moment().diff(serving.start));
                new_duration = d.as('minutes') + minutes;
            } else {
                new_duration = serving.duration + minutes;
            }

            const socket_id = await this.getSocketId();

            const queryParams = {
                live_status: true,
                queuer_id: serving.id,
                socket_id
            };
            const params = {
                duration: new_duration
            };
            const response = await this.live_status.$put('queuer', queryParams, params);
            const live_status = await response.$get('live_status');
            this.parseStatus(live_status);
            return this;
        } finally {
            this.waiting_on_update = false;
            Mutex.unlock(mutex);
        }
    }

    addQueuerAndUpdate(newQueuer) {
        return this.addNewQueuer(newQueuer).then(this.updateLiveStatusFromAddQueuer);
    }

    async addNewQueuer(newQueuer) {
        this.waiting_on_update = true;
        newQueuer.socket_id = await this.getSocketId();
        return this.company.$post('queuers', {live_status: true}, newQueuer)
            .catch((err) => {
                this.waiting_on_update = false;
                throw err;
            });
    }

    updateLiveStatusFromAddQueuer(queuer) {
        this.waiting_on_update = true;
        return queuer.$get('live_status')
            .then((live_status) => {
                this.parseStatus(live_status);
                this.waiting_on_update = false;
                return this;
            });
    }

    async $delete(queuer) {
        this.waiting_on_update = true;
        try {
            const socket_id = await this.getSocketId();
            const response = await this.live_status.$del('queuer', {live_status: true, queuer_id: queuer.id, socket_id});
            const live_status = await response.$get('live_status');
            this.parseStatus(live_status);
            return this;
        } finally {
            this.waiting_on_update = false;
        }
    }

    async getSocketId() {
        try {
            const pusher = await this.company.pusherConnection.promise;
            return pusher.connection.socket_id;
        } catch (error) {
            console.warn('Failed to get pusher socket_id');
            console.warn(error);
            return null;
        }
    }

}

export {
    LiveStatus
};


